import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { AutorenewOutlined } from '@mui/icons-material';
import FuseDialog from '@fuse/core/FuseDialog';
import { closeDialog } from 'app/store/fuse/dialogSlice';
import DIALOGS from 'app/constants/dialogs';
import { TRANSLATION_KEY } from '../constants';

const RenewSubscriptionDialog = ({ onConfirm, isLoading }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation(TRANSLATION_KEY);

  const onClose = useCallback(() => {
    dispatch(closeDialog());
  }, [dispatch]);

  const handleConfirm = () => {
    if (isLoading) return;
    onConfirm?.();
  };

  return (
    <FuseDialog
      fullWidth
      maxWidth="sm"
      dialogId={DIALOGS.RENEW_CONFIRM}
      onClose={onClose}
      PaperProps={{ sx: { maxWidth: '701px', borderRadius: '16px' } }}
    >
      <Box padding="32px" display="flex" flexDirection="column" gap="24px">
        {/* ── Header ── */}
        <Box display="flex" flexDirection="column" gap="12px">
          <Box display="flex" alignItems="center" gap="10px">
            {/* Renew icon — green circle with refresh arrow */}
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                bgcolor: '#1a6b3c',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <AutorenewOutlined sx={{ fontSize: '14px', color: 'white' }} />
            </Box>

            <Typography variant="h5" fontWeight={700} color="accent.900" lineHeight="32px" fontSize="23px">
              {t('RENEW_SUBSCRIPTION_TITLE')}
            </Typography>
          </Box>

          {/* Description */}
          <Typography variant="body2" color="accent.500" fontSize="15px" lineHeight="20px" fontWeight={400}>
            {t('RENEW_SUBSCRIPTION_DESC_1')}
            <br />
            <br />
            {t('RENEW_SUBSCRIPTION_DESC_2')}
          </Typography>
        </Box>

        {/* ── Action buttons ── */}
        <Box display="flex" gap="12px" alignItems="center">
          <Button
            variant="contained"
            color="secondary"
            disabled={isLoading}
            onClick={handleConfirm}
            startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{
              borderRadius: '20px',
              textTransform: 'none',
              px: '16px',
              py: '10px',
              fontSize: '14px',
              fontWeight: 500,
            }}
          >
            {t('RENEW_SUBSCRIPTION_CONFIRM')}
          </Button>
          <Button
            onClick={onClose}
            sx={{
              textTransform: 'none',
              color: 'accent.900',
              borderRadius: '20px',
              px: '16px',
              py: '10px',
              fontSize: '14px',
              fontWeight: 500,
            }}
          >
            {t('KEEP_CANCELLATION')}
          </Button>
        </Box>
      </Box>
    </FuseDialog>
  );
};

export default RenewSubscriptionDialog;
